import React, { createContext, useContext, ReactNode } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

interface AnalyticsContextType {
  trackPageView: (params?: any) => void;
  trackEvent: (eventName: string, parameters?: any) => void;
  trackPurchase: (
    transactionId: string,
    value: number,
    currency: string,
    items: any[]
  ) => void;
  trackAddToCart: (currency: string, value: number, items: any[]) => void;
  trackRemoveFromCart: (currency: string, value: number, items: any[]) => void;
  trackViewItem: (currency: string, value: number, items: any[]) => void;
  trackViewItemList: (itemListName: string, items: any[]) => void;
  trackSelectItem: (itemListName: string, items: any[]) => void;
  trackBeginCheckout: (currency: string, value: number, items: any[]) => void;
  trackSearch: (searchTerm: string) => void;
  trackSignUp: (method: string) => void;
  trackLogin: (method: string) => void;
  trackRecipeView: (
    recipeId: string,
    recipeName: string,
    category: string
  ) => void;
  trackMenuPlanGenerate: (preferences: string[]) => void;
  trackFilterUsage: (filterType: string, filterValue: string) => void;
  trackLanguageChange: (oldLanguage: string, newLanguage: string) => void;
  trackNewsletterSignup: (location: string) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined
);

interface AnalyticsProviderProps {
  children: ReactNode;
  measurementId: string;
  debug?: boolean;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  measurementId,
  debug = false,
}) => {
  const analytics = useAnalytics({ measurementId, debug });

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error(
      'useAnalyticsContext must be used within an AnalyticsProvider'
    );
  }
  return context;
};
